import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Users, Mail, FileText, Handshake, HandCoins, Loader2, Calendar, Layers, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ResourcesManager } from "@/components/admin/ResourcesManager";
import { EventsManager } from "@/components/admin/EventsManager";
import { CategoriesManager } from "@/components/admin/CategoriesManager";
import { SubmissionsManager } from "@/components/admin/SubmissionsManager";

const TABS = [
  { key: "resources", label: "Resources", el: <ResourcesManager /> },
  { key: "events", label: "Events", el: <EventsManager /> },
  { key: "categories", label: "Categories", el: <CategoriesManager /> },
  { key: "applications", label: "Applications", el: <SubmissionsManager name="applications" /> },
  { key: "volunteers", label: "Volunteers", el: <SubmissionsManager name="volunteers" /> },
  { key: "sponsors", label: "Sponsors", el: <SubmissionsManager name="sponsors" /> },
  { key: "rsvps", label: "RSVPs", el: <SubmissionsManager name="rsvps" /> },
  { key: "newsletter", label: "Newsletter", el: <SubmissionsManager name="newsletter" /> },
];

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("resources");

  useEffect(() => { if (user === false) nav("/login"); }, [user, nav]);
  useEffect(() => { if (user?.role === "admin") api.get("/admin/summary").then((r) => setSummary(r.data)).catch(() => {}); }, [user, tab]);

  if (!user) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-navy-900" /></div>;

  const doLogout = async () => { await logout(); nav("/"); };

  const STAT = summary ? [
    { l: "Resources", v: summary.resources, icon: HandCoins },
    { l: "Applications", v: summary.applications, icon: FileText },
    { l: "Volunteers", v: summary.volunteers, icon: Users },
    { l: "Sponsors", v: summary.sponsors, icon: Handshake },
    { l: "Subscribers", v: summary.newsletter, icon: Mail },
    { l: "Donations", v: summary.donations, icon: Layers },
  ] : [];

  return (
    <main className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <div className="bg-navy-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
          <div><span className="font-display text-xl font-bold">Admin CMS</span><p className="text-xs text-slate-400">{user.email}</p></div>
          <div className="flex gap-2">
            <Button onClick={() => nav("/")} variant="ghost" className="rounded-full text-white hover:bg-white/10" data-testid="admin-view-site"><ExternalLink className="h-4 w-4 mr-2" /> View Site</Button>
            <Button onClick={doLogout} variant="outline" className="rounded-full bg-transparent border-white/30 text-white hover:bg-white hover:text-navy-900" data-testid="admin-logout"><LogOut className="h-4 w-4 mr-2" /> Sign Out</Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
          {STAT.map((s, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`admin-stat-${s.l.toLowerCase()}`}>
              <s.icon className="h-5 w-5 text-gold mb-3" />
              <div className="font-display text-2xl font-bold text-navy-900">{s.v}</div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{s.l}</div>
            </div>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white rounded-full p-1 flex-wrap h-auto justify-start">
            {TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`admin-tab-${t.key}`} className="rounded-full data-[state=active]:bg-navy-900 data-[state=active]:text-white">{t.label}</TabsTrigger>
            ))}
          </TabsList>
          {TABS.map((t) => <TabsContent key={t.key} value={t.key} className="mt-6">{tab === t.key && t.el}</TabsContent>)}
        </Tabs>
      </div>
    </main>
  );
}
